import { getEnvConfig } from '@/config/env.config';
import axios from 'axios';
import React from 'react';
import { useNavigate } from 'react-router-dom';


const config = getEnvConfig();
const API_URL = config.DEBUG_MODE ? '/api' : config.API_URL;

export const api = axios.create({
    baseURL: API_URL,
    withCredentials: true
});

// Intercept responses to handle token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If error is not 401 or request has already been retried, reject
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
            // Attempt to refresh the token
            await axios.post('/api/auth/token/refresh/', {}, { withCredentials: true });

            // Retry the original request
            return api(originalRequest);
        } catch (refreshError) {
            // If refresh fails, clear user state and redirect to log in
            userStore.clearUser();
            const navigate = useNavigate();
            navigate('/signin');
            return Promise.reject(refreshError);
        }
    }
);

// User store for global state management
export const userStore = {
    user: '',
    subscribers: new Set(),
    loading: false,

    setUser(userData) {
        this.user = userData;
        this.notifySubscribers();
    },

    clearUser() {
        this.user = null;
        this.notifySubscribers();
    },

    subscribe(callback) {
        this.subscribers.add(callback);
        return () => this.subscribers.delete(callback);
    },

    notifySubscribers() {
        this.subscribers.forEach(callback => callback(this.user));
    },

    async loadUser() {
        if (this.loading || this.user) return;

        this.loading = true;
        try {
            const response = await api.get('/auth/user/');
            this.setUser(response.data);
        } catch (error) {
            this.clearUser();
        } finally {
            this.loading = false;
        }
    }
};

// React hook for accessing user state
export const useUser = () => {
    const [user, setUser] = React.useState(userStore.user);

    React.useEffect(() => {
        if (!user) {
            userStore.loadUser();
        }
        return userStore.subscribe(setUser);
    }, []);

    return user;
};

// Authentication service methods
export const authService = {
    async login(email, password) {
        const response = await api.post('/auth/login/', { email, password });
        userStore.setUser(response.data.user);
        return response.data;
    },

    async register(email, password, marketingConsent) {
        const response = await api.post('/auth/registration/', {
            email,
            password1: password,
            password2: password,
            marketing_emails_enabled: marketingConsent
        });
        userStore.setUser(response.data.user);
        await this.login(email, password);
        return response.data;
    },

    async logout() {
        await api.post('/auth/logout/');
        userStore.clearUser();
        const navigate = useNavigate();
        navigate('/signin');
    },

    handleGoogleAuth() {
        // Generate a random state for CSRF protection
        const state = Math.random().toString(36).substring(7);
        localStorage.setItem('oauth_state', state);

        // Google OAuth URL parameters
        const params = new URLSearchParams({
            state,
            redirect_uri: `${window.location.origin}/account/social/google/`,
            response_type: 'code',
            client_id: config.GOOGLE_CLIENT_ID,
            scope: 'email profile',
        });

        // Redirect to Google OAuth
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    },

     async handleOAuthCallback(code, state) {
        // Verify state matches to prevent CSRF
        const savedState = localStorage.getItem('oauth_state');
        if (state !== savedState) {
            throw new Error('Invalid OAuth state');
        }

        try {
            const response = await api.post('/account/social/google/', { code });
            userStore.setUser(response.data.user);
            return response.data;
        } catch (error) {
            console.error('OAuth error:', error);
            throw error;
        } finally {
            localStorage.removeItem('oauth_state');
        }
    }
};
